import { useState, useEffect, useRef } from 'react';
import { videos } from '../../mocks/videos';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import VideoUploadModal from './components/VideoUploadModal';
import SEOHead from '../../components/feature/SEOHead';

interface Video {
  id: string;
  title: string;
  youtubeId: string;
  category: string;
  views: number;
  likes: number;
  uploadDate: string;
  description: string;
}

const categories = [
  { id: 'all', label: 'All Videos', emoji: '🎬' },
  { id: 'funny', label: 'Funny', emoji: '😂' },
  { id: 'cute', label: 'Cute', emoji: '😻' },
  { id: 'educational', label: 'Educational', emoji: '📚' },
  { id: 'rescue', label: 'Rescue Stories', emoji: '❤️' },
  { id: 'compilation', label: 'Compilations', emoji: '🎞️' },
];

export default function VideosPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeVideo, setActiveVideo] = useState<Video>(videos[0]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const [autoplayCountdown, setAutoplayCountdown] = useState<number | null>(null);
  const [isPlaylistOpen, setIsPlaylistOpen] = useState(false);
  const [shareToast, setShareToast] = useState(false);
  const [ytReady, setYtReady] = useState(false);
  const playerRef = useRef<any>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const filteredVideos = selectedCategory === 'all' 
    ? videos 
    : videos.filter(v => v.category === selectedCategory);

  // Get video count per category
  const getCategoryCount = (categoryId: string) => {
    if (categoryId === 'all') return videos.length;
    return videos.filter(v => v.category === categoryId).length;
  };

  // Initialize YouTube API
  useEffect(() => {
    if (!(window as any).YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    (window as any).onYouTubeIframeAPIReady = () => {
      setYtReady(true);
    };

    if ((window as any).YT?.Player) {
      setYtReady(true);
    }
  }, []);

  // Create/update player when activeVideo changes
  useEffect(() => {
    if (!ytReady || !activeVideo) return;

    if (playerRef.current) {
      playerRef.current.loadVideoById(activeVideo.youtubeId);
    } else {
      playerRef.current = new (window as any).YT.Player('youtube-player', {
        videoId: activeVideo.youtubeId,
        playerVars: {
          autoplay: 0,
          modestbranding: 1,
          rel: 0,
        },
        events: {
          onStateChange: (event: any) => {
            if (event.data === (window as any).YT.PlayerState.ENDED && autoplayEnabled) {
              startAutoplayCountdown();
            }
          },
        },
      });
    }
  }, [ytReady, activeVideo]);

  const startAutoplayCountdown = () => {
    const currentIndex = filteredVideos.findIndex(v => v.id === activeVideo.id);
    if (currentIndex < filteredVideos.length - 1) {
      setAutoplayCountdown(5);
      countdownTimerRef.current = setInterval(() => {
        setAutoplayCountdown(prev => {
          if (prev === null || prev <= 1) {
            if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
            playNextVideo();
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const cancelAutoplay = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setAutoplayCountdown(null);
  };

  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  const playNextVideo = () => {
    const currentIndex = filteredVideos.findIndex(v => v.id === activeVideo.id);
    if (currentIndex < filteredVideos.length - 1) {
      setActiveVideo(filteredVideos[currentIndex + 1]);
      cancelAutoplay();
    }
  };

  const playPreviousVideo = () => {
    const currentIndex = filteredVideos.findIndex(v => v.id === activeVideo.id);
    if (currentIndex > 0) {
      setActiveVideo(filteredVideos[currentIndex - 1]);
      cancelAutoplay();
    }
  };

  const handleShareVideo = () => {
    const youtubeUrl = `https://www.youtube.com/watch?v=${activeVideo.youtubeId}`;
    navigator.clipboard.writeText(youtubeUrl);
    setShareToast(true);
    setTimeout(() => setShareToast(false), 3000);
  };

  const currentIndex = filteredVideos.findIndex(v => v.id === activeVideo.id);
  const isLastVideo = currentIndex === filteredVideos.length - 1;

  return (
    <>
      <SEOHead
        title="Cat Videos - Watch Funny, Cute & Educational Cat Content | MeowCentral"
        description="Watch the best cat videos online! From hilarious cat fails to heartwarming rescue stories, educational care tips, and adorable kitten compilations. New videos added weekly."
        keywords="cat videos, funny cat videos, cute cats, cat compilations, rescue cats, educational cat content, kitten videos"
        canonical="https://meowcentral.com/videos"
      />
      <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-amber-50">
        <Navbar />
        
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Cat Videos</h1>
                <p className="text-gray-600">Watch, enjoy, and share the best cat content</p>
              </div>
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className="mt-4 sm:mt-0 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-upload-cloud-line text-xl"></i>
                Upload Video
              </button>
            </div>

            {/* Category Filter */}
            <div className="sticky top-16 bg-white/95 backdrop-blur-sm z-40 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-4 mb-8 border-b border-gray-200 shadow-sm">
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      const newFilteredVideos = cat.id === 'all' ? videos : videos.filter(v => v.category === cat.id);
                      if (newFilteredVideos.length > 0) {
                        setActiveVideo(newFilteredVideos[0]);
                      }
                    }}
                    className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                      selectedCategory === cat.id
                        ? 'bg-orange-500 text-white shadow-lg shadow-orange-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{cat.emoji}</span>
                    <span>{cat.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      selectedCategory === cat.id ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                      {getCategoryCount(cat.id)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Video Player Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Player */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  {/* Player Container */}
                  <div className="relative bg-black aspect-video">
                    <div id="youtube-player" className="w-full h-full"></div>
                    
                    {/* Autoplay Countdown Overlay */}
                    {autoplayCountdown !== null && autoplayEnabled && (
                      <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-10">
                        <div className="text-center">
                          <div className="text-white text-6xl font-bold mb-4">{autoplayCountdown}</div>
                          <p className="text-white text-xl mb-6">Next video playing soon...</p>
                          <button
                            onClick={cancelAutoplay}
                            className="bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* End of Playlist Message */}
                    {isLastVideo && !autoplayEnabled && (
                      <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg p-4 text-center">
                        <p className="text-white font-medium mb-2">🎬 You've reached the end of this playlist</p>
                        <button
                          onClick={() => setActiveVideo(filteredVideos[0])}
                          className="text-orange-400 hover:text-orange-300 text-sm font-medium cursor-pointer whitespace-nowrap"
                        >
                          Start from beginning
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Video Info */}
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">
                      {activeVideo.title}
                    </h2>
                    
                    <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {activeVideo.views.toLocaleString()} views
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line"></i>
                        {activeVideo.likes.toLocaleString()} likes
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {new Date(activeVideo.uploadDate).toLocaleDateString()}
                      </span>
                    </div>

                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {activeVideo.description}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleShareVideo}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-share-line"></i>
                        Share Video
                      </button>
                      <a
                        href={`https://www.youtube.com/watch?v=${activeVideo.youtubeId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-full font-medium transition-colors whitespace-nowrap"
                      >
                        <i className="ri-youtube-line"></i>
                        Watch on YouTube
                      </a>
                    </div>
                  </div>
                </div>

                {/* Player Controls */}
                <div className="mt-6 flex items-center justify-between bg-white rounded-xl p-4 shadow-lg">
                  <button
                    onClick={playPreviousVideo}
                    disabled={currentIndex === 0}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                      currentIndex === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-orange-500 hover:bg-orange-600 text-white'
                    }`}
                  >
                    <i className="ri-skip-back-line"></i>
                    Previous
                  </button>

                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 font-medium">
                      {currentIndex + 1} / {filteredVideos.length}
                    </span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoplayEnabled}
                        onChange={(e) => setAutoplayEnabled(e.target.checked)}
                        className="w-4 h-4 text-orange-500 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-700 font-medium whitespace-nowrap">Autoplay</span>
                    </label>
                  </div>

                  <button
                    onClick={playNextVideo}
                    disabled={isLastVideo}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                      isLastVideo
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-orange-500 hover:bg-orange-600 text-white'
                    }`}
                  >
                    Next
                    <i className="ri-skip-forward-line"></i>
                  </button>
                </div>
              </div>

              {/* Playlist Sidebar - Desktop */}
              <div className="hidden lg:block">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-32 max-h-[calc(100vh-10rem)]">
                  <div className="p-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white">
                    <h3 className="font-bold text-lg">Playlist</h3>
                    <p className="text-sm text-white/90">{filteredVideos.length} videos</p>
                  </div>
                  <div className="overflow-y-auto max-h-[calc(100vh-16rem)] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    {filteredVideos.map((video, index) => (
                      <button
                        key={video.id}
                        onClick={() => {
                          setActiveVideo(video);
                          cancelAutoplay();
                        }}
                        className={`w-full p-3 flex gap-3 hover:bg-gray-50 transition-colors cursor-pointer group ${
                          video.id === activeVideo.id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                        }`}
                        title={video.title}
                      >
                        <div className="relative flex-shrink-0">
                          <img
                            src={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
                            alt={video.title}
                            className="w-32 h-20 object-cover rounded-lg"
                          />
                          {video.id === activeVideo.id && (
                            <div className="absolute inset-0 bg-orange-500/20 rounded-lg flex items-center justify-center">
                              <i className="ri-play-circle-fill text-3xl text-white"></i>
                            </div>
                          )}
                          <div className="absolute top-1 left-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                            #{index + 1}
                          </div>
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <h4 className={`font-medium text-sm mb-1 group-hover:text-orange-600 transition-colors ${
                            video.id === activeVideo.id ? 'text-orange-600' : 'text-gray-900'
                          }`}>
                            {video.title}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {video.views.toLocaleString()} views
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile Playlist Button */}
              <button
                onClick={() => setIsPlaylistOpen(true)}
                className="lg:hidden fixed bottom-6 right-6 bg-orange-500 hover:bg-orange-600 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center z-40 cursor-pointer"
              >
                <i className="ri-play-list-2-fill text-2xl"></i>
              </button>

              {/* Mobile Playlist Drawer */}
              {isPlaylistOpen && (
                <div className="lg:hidden fixed inset-0 z-50 flex items-end">
                  <div
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    onClick={() => setIsPlaylistOpen(false)}
                  ></div>
                  <div className="relative bg-white w-full max-h-[80vh] rounded-t-3xl shadow-2xl overflow-hidden animate-slide-up">
                    <div className="sticky top-0 p-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white flex items-center justify-between z-10">
                      <div>
                        <h3 className="font-bold text-lg">Playlist</h3>
                        <p className="text-sm text-white/90">{filteredVideos.length} videos</p>
                      </div>
                      <button
                        onClick={() => setIsPlaylistOpen(false)}
                        className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-[calc(80vh-5rem)] pb-6">
                      {filteredVideos.map((video, index) => (
                        <button
                          key={video.id}
                          onClick={() => {
                            setActiveVideo(video);
                            setIsPlaylistOpen(false);
                            cancelAutoplay();
                          }}
                          className={`w-full p-4 flex gap-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                            video.id === activeVideo.id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                          }`}
                        >
                          <div className="relative flex-shrink-0">
                            <img
                              src={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
                              alt={video.title}
                              className="w-32 h-20 object-cover rounded-lg"
                            />
                            {video.id === activeVideo.id && (
                              <div className="absolute inset-0 bg-orange-500/20 rounded-lg flex items-center justify-center">
                                <i className="ri-play-circle-fill text-3xl text-white"></i>
                              </div>
                            )}
                            <div className="absolute top-1 left-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                              #{index + 1}
                            </div>
                          </div>
                          <div className="flex-1 text-left">
                            <h4 className={`font-medium text-sm mb-1 ${
                              video.id === activeVideo.id ? 'text-orange-600' : 'text-gray-900'
                            }`}>
                              {video.title}
                            </h4>
                            <p className="text-xs text-gray-500">
                              {video.views.toLocaleString()} views
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
        <VideoUploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} />

        {/* Share Toast */}
        {shareToast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-full shadow-2xl z-50 flex items-center gap-2 animate-slide-up">
            <i className="ri-check-line text-green-400 text-xl"></i>
            <span className="font-medium">Link copied to clipboard!</span>
          </div>
        )}
      </div>
    </>
  );
}